import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Inbox, Wrench, HandHeart, CheckCircle2, ChevronRight, Loader2 } from "lucide-react";
import { getActionCenter } from "@/lib/api";

const TILES = [
  { key: "open", label: "Open", icon: Inbox, tint: "primary" },
  { key: "in_progress", label: "In progress", icon: Wrench, tint: "amber" },
  { key: "waiting_on_me", label: "Waiting on me", icon: HandHeart, tint: "terracotta" },
  { key: "completed_this_month", label: "Done this month", icon: CheckCircle2, tint: "success" },
];

const TINT = {
  primary: { bg: "var(--ta-accent)", fg: "var(--ta-primary)" },
  amber: { bg: "#FFF3D6", fg: "#8A5A00" },
  terracotta: { bg: "#FCE6DE", fg: "var(--ta-secondary)" },
  success: { bg: "var(--ta-success-bg)", fg: "var(--ta-success)" },
};

export default function ActionCenter() {
  const [counts, setCounts] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ready | error

  useEffect(() => {
    let alive = true;
    getActionCenter()
      .then((c) => {
        if (!alive) return;
        setCounts(c);
        setStatus("ready");
      })
      .catch(() => {
        if (!alive) return;
        setStatus("error");
      });
    return () => { alive = false; };
  }, []);

  const noActions =
    counts &&
    !counts.open &&
    !counts.in_progress &&
    !counts.waiting_on_me &&
    !counts.completed_this_month;

  const heading = noActions ? "Action Center" : "A few small wins waiting.";

  return (
    <section className="mt-10" data-testid="action-center-section">
      <div className="flex items-end justify-between mb-4">
        <div>
          <div className="text-xs uppercase tracking-widest font-semibold" style={{ color: "var(--ta-secondary)" }}>
            Action Center
          </div>
          <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight mt-1" style={{ color: "var(--ta-primary)" }} data-testid="action-center-heading">
            {status === "loading" ? "Action Center" : heading}
          </h2>
        </div>
      </div>

      {status === "loading" && (
        <div className="flex items-center gap-2 text-sm" style={{ color: "var(--ta-text-secondary)" }} data-testid="action-center-loading">
          <Loader2 size={16} className="animate-spin" /> Loading your actions…
        </div>
      )}

      {status === "error" && (
        <div className="rounded-2xl border p-5 bg-white" style={{ borderColor: "var(--ta-border)" }} data-testid="action-center-error">
          <div className="text-base" style={{ color: "var(--ta-primary)" }}>
            We couldn't load your actions right now.
          </div>
          <div className="text-sm mt-1" style={{ color: "var(--ta-text-secondary)" }}>
            Please try again shortly.
          </div>
        </div>
      )}

      {status === "ready" && (
        <>
          <div className="grid grid-cols-2 gap-3" data-testid="action-center-tiles">
            {TILES.map((t) => {
              const tint = TINT[t.tint];
              const value = counts[t.key] ?? 0;
              return (
                <div
                  key={t.key}
                  data-testid={`action-tile-${t.key}`}
                  className="rounded-2xl bg-white border p-5 min-h-[110px] flex flex-col justify-between"
                  style={{ borderColor: "var(--ta-border)" }}
                >
                  <div
                    className="w-10 h-10 rounded-xl inline-flex items-center justify-center"
                    style={{ background: tint.bg, color: tint.fg }}
                  >
                    <t.icon size={20} strokeWidth={2.2} />
                  </div>
                  <div>
                    <div className="font-display text-3xl font-semibold tabular-nums leading-none" style={{ color: "var(--ta-primary)" }}>
                      {value}
                    </div>
                    <div className="text-sm mt-1" style={{ color: "var(--ta-text-secondary)" }}>
                      {t.label}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {noActions ? (
            <p className="mt-4 text-sm" style={{ color: "var(--ta-text-secondary)" }} data-testid="action-center-empty">
              No actions need your attention yet.
            </p>
          ) : (
            <Link
              to="/brief"
              data-testid="action-center-open-brief"
              className="mt-4 inline-flex items-center gap-1 text-sm font-medium"
              style={{ color: "var(--ta-primary)" }}
            >
              Open brief <ChevronRight size={16} />
            </Link>
          )}
        </>
      )}
    </section>
  );
}
